import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Layout } from "../components/layout/Layout";
import { Section, SectionHeader } from "../components/common/Section";
import { HiX, HiChevronLeft, HiChevronRight } from "react-icons/hi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const [products, setProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filters = [
    { id: "all", name: "All" },
    { id: "fusion", name: "Fusion" },
    { id: "platinum", name: "Platinum" },
    { id: "diamond", name: "Diamond" },
  ];

  const filteredProducts = activeFilter === "all" 
    ? products 
    : products.filter(p => p.category === activeFilter);

  const openLightbox = (product) => {
    setSelectedImage(product);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const currentIndex = filteredProducts.findIndex(p => p.id === selectedImage.id);
    if (direction === "prev" && currentIndex > 0) {
      setSelectedImage(filteredProducts[currentIndex - 1]);
    } else if (direction === "next" && currentIndex < filteredProducts.length - 1) {
      setSelectedImage(filteredProducts[currentIndex + 1]);
    }
  };

  const currentIndex = selectedImage ? filteredProducts.findIndex(p => p.id === selectedImage.id) : -1;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden" data-testid="gallery-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal to-obsidian" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium text-gold-400 bg-gold-500/10 rounded-full mb-6 tracking-wider uppercase border border-gold-500/20">
              Gallery
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white">
              Our <span className="text-gold-gradient">Product Gallery</span>
            </h1>
            <p className="mt-6 text-gray-400 text-lg max-w-2xl mx-auto">
              Browse through our collection of premium gaming machines and installations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      <Section background="default">
        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-4 mb-16" data-testid="gallery-filters">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              data-testid={`gallery-filter-${filter.id}`}
              className={`px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 ${
                activeFilter === filter.id
                  ? "bg-gradient-to-r from-gold-600 to-gold-500 text-obsidian shadow-gold-glow"
                  : "bg-surface border border-white/10 text-gray-300 hover:border-gold-500/30 hover:text-white"
              }`}
            >
              {filter.name}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="aspect-square rounded-xl bg-surface animate-pulse" />
            ))}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              data-testid="gallery-grid"
            >
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => openLightbox(product)}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer"
                  data-testid={`gallery-item-${product.id}`}
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div>
                      <span className="text-xs text-gold-400 uppercase">{product.category}</span>
                      <h3 className="text-white font-medium">{product.name}</h3>
                    </div>
                  </div>
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-gold-500/50 rounded-xl transition-colors duration-300" />
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}

        {!loading && filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No images found in this category.</p>
          </div>
        )}
      </Section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            <div className="absolute inset-0 bg-obsidian/95 backdrop-blur-xl" />

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative z-10 max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute -top-12 right-0 p-2 text-white hover:text-gold-400 transition-colors"
                aria-label="Close"
              >
                <HiX className="text-3xl" />
              </button>

              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden bg-charcoal">
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.name}
                  className="w-full max-h-[80vh] object-contain"
                />

                {/* Navigation */}
                {currentIndex > 0 && (
                  <button
                    onClick={() => navigateImage("prev")}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10"
                    aria-label="Previous"
                  >
                    <HiChevronLeft className="text-white text-2xl" />
                  </button>
                )}
                {currentIndex < filteredProducts.length - 1 && (
                  <button
                    onClick={() => navigateImage("next")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-obsidian/80 hover:bg-gold-500/20 transition-colors border border-white/10"
                    aria-label="Next"
                  >
                    <HiChevronRight className="text-white text-2xl" />
                  </button>
                )}
              </div>

              {/* Info */}
              <div className="mt-4 text-center">
                <span className="text-gold-400 text-sm uppercase">{selectedImage.category} Series</span>
                <h3 className="text-white text-xl font-heading mt-1">{selectedImage.name}</h3>
                <p className="text-gray-400 text-sm mt-2">{currentIndex + 1} / {filteredProducts.length}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default Gallery;
